import {
  defineComponent,
  h,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  type PropType,
} from 'vue';
import type { PagiflowInstance, PagiflowOptions } from './types';

type PagiflowCtor = (
  selector: string | HTMLElement,
  opts?: PagiflowOptions
) => PagiflowInstance;

function getPagiflow(): PagiflowCtor | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow as PagiflowCtor;
  }
  try {
    const mod = require('pagiflow');
    return (mod.default ?? mod) as PagiflowCtor;
  } catch {
    return null;
  }
}

function initPagiflowFromElement(
  ctor: PagiflowCtor,
  el: HTMLElement,
  opts: PagiflowOptions
): PagiflowInstance | null {
  const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
  const selector = `[data-pagiflow-vue-id="${attrValue}"]`;
  el.setAttribute('data-pagiflow-vue-id', attrValue);

  const bySelector = ctor(selector, opts) as PagiflowInstance | null;
  if (!bySelector) {
    el.removeAttribute('data-pagiflow-vue-id');
    return null;
  }
  return bySelector;
}

export const Pagiflow = defineComponent({
  name: 'Pagiflow',
  props: {
    options: {
      type: Object as PropType<PagiflowOptions>,
      default: () => ({}),
    },
    tag: {
      type: String,
      default: 'div',
    },
  },
  emits: ['slideChange', 'ready'],
  setup(props, { slots, attrs, expose, emit }) {
    const rootRef = ref<HTMLElement | null>(null);
    const instance = ref<PagiflowInstance | null>(null);
    const currentIndex = ref(props.options.startIndex ?? 0);

    let resizeObserver: ResizeObserver | null = null;
    let raf1 = 0;
    let raf2 = 0;
    let timeoutId = 0;

    const next = () => instance.value?.next();
    const prev = () => instance.value?.prev();
    const goTo = (index: number, opts?: { silent?: boolean; instant?: boolean }) =>
      instance.value?.goTo(index, opts);
    const play = () => instance.value?.play();
    const pause = () => instance.value?.pause();
    const togglePlayPause = () => instance.value?.togglePlayPause();
    const setOptions = (opts: Partial<PagiflowOptions>, rebuild = false) =>
      instance.value?.setOptions(opts, rebuild);
    const reInit = (opts: Partial<PagiflowOptions> = {}) => instance.value?.reInit(opts);

    expose({
      instance,
      currentIndex,
      next,
      prev,
      goTo,
      play,
      pause,
      togglePlayPause,
      setOptions,
      reInit,
    });

    onMounted(() => {
      const el = rootRef.value;
      if (!el) return;

      const existing = (el as any).pagiflowInstance as PagiflowInstance | undefined;
      if (existing && !existing.destroyed) {
        instance.value = existing;
        return;
      }

      const ctor = getPagiflow();
      if (!ctor) {
        console.error('[Pagiflow Vue] Core not found. Install pagiflow or load it via CDN.');
        return;
      }

      const effectiveOptions = { ...props.options };
      const inst = initPagiflowFromElement(ctor, el, effectiveOptions);
      if (!inst) return;

      const syncLayout = () => {
        const api = inst as PagiflowInstance & { update?: () => void };
        api.update?.();
      };

      raf1 = window.requestAnimationFrame(syncLayout);
      raf2 = window.requestAnimationFrame(syncLayout);
      timeoutId = window.setTimeout(syncLayout, 80);
      window.addEventListener('load', syncLayout, { once: true });

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncLayout);
        resizeObserver.observe(el);
      }

      inst.onSlideChange((idx) => {
        currentIndex.value = idx;
        emit('slideChange', idx);
      });

      instance.value = inst;
      emit('ready', inst);
    });

    watch(
      () => props.options,
      (nextOptions) => {
        if (instance.value) {
          instance.value.setOptions(nextOptions, true);
        }
      },
      { deep: true }
    );

    onBeforeUnmount(() => {
      const el = rootRef.value;
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(timeoutId);
      resizeObserver?.disconnect();

      if (instance.value && !instance.value.destroyed) {
        try {
          instance.value.destroy();
        } catch {
          // ignore unmount destroy errors
        }
      }

      if (el) delete (el as any).pagiflowInstance;
      instance.value = null;
    });

    return () =>
      h(
        props.tag,
        {
          ...attrs,
          ref: rootRef,
          class: ['pagiflow', attrs.class],
        },
        slots.default ? slots.default() : []
      );
  },
});
